import './SellerFilingOngoingItem.scss';
import { formatDate, formatNumber } from '../../../core/helpers';
import CountryLabel from '../../../components/CountryLabel/CountryLabel';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PaymentIsntructionIcon } from '../../../icons/payment-instruction.svg';
import { ReactComponent as FolderIcon } from '../../../icons/folder-outline.svg';
import { SellerFilingReportToConfirm } from '../../../core/types';
import Tooltip from '../../../components/Tooltip/Tooltip';
import { normalizeDateFE } from '../../../core/normalization';
import Button from '../../../components/Button';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface Props {
  report: SellerFilingReportToConfirm;
  onFolderBtnClick: () => void;
  onBtnClick: () => void;
  onClickAgenda: () => void;
}

export default function SellerFilingOngoingItem({
  report,
  onFolderBtnClick,
  onBtnClick,
  onClickAgenda,
}: Props) {
  const { t } = useTranslation();
  const { vs2912CpPrototypeV2 } = useFlags();

  const instruction = report.payment_instruction;

  const renderPeriod = () => {
    let formatStr = 'MMMM yyyy';

    if (report.period.type === 'year') {
      formatStr = 'yyyy';
    }

    if (report.period.type === 'quarter') {
      formatStr = 'QQQ yyyy';
    }

    if (
      report.country.short_name === 'IT' &&
      report.period.type !== 'quarter'
    ) {
      return t('Payment for *period*').replace(
        '*period*',
        formatDate(normalizeDateFE(report.period.from_date), formatStr)
      );
    }

    return (
      <>
        {t('Reporting period')}:{' '}
        <span className='fw-600'>
          {formatDate(normalizeDateFE(report.period.from_date), formatStr)}
        </span>
      </>
    );
  };

  return (
    <div className='SellerFilingOngoingItem p-3 mb-3'>
      <div className='d-flex justify-content-between'>
        <div
          style={{
            maxWidth: '40%',
          }}
          className='text-trancate'
        >
          <CountryLabel
            country={report.type === 'oss' ? 'EU' : report.country.short_name}
            className='fw-800 me-2'
          />
        </div>
        <div className='d-flex flex-wrap justify-content-end'>
          <div>
            {renderPeriod()}
            <span className='SellerFilingOngoingItem__delimeter'>|</span>
          </div>
          <div>
            {t('Deadline')}:{' '}
            <span className='fw-600'>
              {formatDate(
                normalizeDateFE(
                  report.payment_instruction?.due_date || report.deadline_date
                ),
                'do MMMM'
              )}
            </span>
          </div>
        </div>
      </div>
      {instruction ? (
        <div className='d-flex justify-content-between mt-3'>
          <div>
            {instruction.confirmed ||
            instruction.is_sepa ||
            instruction.is_null_report ||
            instruction.amount.amount === 0 ? (
              <Button
                styling='outline'
                className='d-inline-flex align-items-center'
                onClick={onBtnClick}
              >
                <PaymentIsntructionIcon className='me-2' />
                <span>
                  {formatNumber(instruction.amount.amount / 100, {
                    currency: instruction.amount.currency,
                  })}
                  &nbsp;
                  {t('Paid').toLowerCase()}
                </span>
              </Button>
            ) : (
              <div className='row row-cols-auto gx-2'>
                <div className='col'>
                  <Button
                    className='d-inline-flex align-items-center'
                    onClick={onBtnClick}
                  >
                    <PaymentIsntructionIcon className='SellerFilingOngoingItem__icon--white me-2' />
                    <span>
                      {formatNumber(instruction.amount.amount / 100, {
                        currency: instruction.amount.currency,
                      })}
                      &nbsp;
                      {t('to pay')}
                    </span>
                  </Button>
                </div>
                {report.country.short_name === 'DE' &&
                  report.type === 'local' &&
                  !!report.payment_instruction && (
                    <div className='col'>
                      <Button styling='outline' onClick={onClickAgenda}>
                        {report.agenda_confirmed
                          ? '✓ Agenda'
                          : t('Submit to Agenda')}
                      </Button>
                    </div>
                  )}
              </div>
            )}
          </div>
          {!!report.confirmation?.documents.length ? (
            <div>
              <button
                className='Modal__btn SellerFilingOngoingItem__folderBtn d-flex'
                onClick={onFolderBtnClick}
              >
                <FolderIcon className='m-auto' />
              </button>
            </div>
          ) : (
            <Tooltip
              content={t('No files for this period')}
              className='Tooltip--SellerFilingOngoingItem'
              delay={500}
            >
              <Button
                styling='naked'
                className='SellerFilingOngoingItem__folderBtn d-flex'
                disabled
              >
                <FolderIcon className='m-auto' />
              </Button>
            </Tooltip>
          )}
        </div>
      ) : (
        vs2912CpPrototypeV2 && (
          <Tooltip content={<>{t('Based on previous periods data')}</>}>
            <Button
              styling='outline'
              className='mt-3 SellerFilingOngoingItem__approxBtn'
            >
              {t('approx. *amount* to pay').replace(
                '*amount*',
                formatNumber(1234, {
                  currency: 'eur',
                })
              )}
            </Button>
          </Tooltip>
        )
      )}
    </div>
  );
}
